import React, { createContext, useContext, useState, useCallback } from "react";

const ExamContext = createContext();

export const useExam = () => {
  const context = useContext(ExamContext);
  if (!context) {
    throw new Error("useExam must be used within an ExamProvider");
  }
  return context;
};

export const ExamProvider = ({ children }) => {
  const [selectedStandard, setSelectedStandard] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedPdfs, setSelectedPdfs] = useState([]);
  const [standards, setStandards] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [pdfList, setPdfList] = useState([]);

  // Set selected standard with full data
  const selectStandard = useCallback((standard) => {
    setSelectedStandard(standard);
    // Reset dependent selections
    setSelectedSubject(null);
    setSelectedPdfs([]);
    setSubjects([]);
    setPdfList([]);
  }, []);

  // Set selected subject with full data
  const selectSubject = useCallback((subject) => {
    setSelectedSubject(subject);
    // Reset dependent selections
    setSelectedPdfs([]);
    setPdfList([]);
  }, []);

  // Toggle PDF selection
  const togglePdf = useCallback((pdf) => {
    setSelectedPdfs((prev) =>
      prev.some((p) => p.id === pdf.id)
        ? prev.filter((p) => p.id !== pdf.id)
        : [...prev, pdf]
    );
  }, []);

  // Clear all selections
  const clearSelections = useCallback(() => {
    setSelectedStandard(null);
    setSelectedSubject(null);
    setSelectedPdfs([]);
    setStandards([]);
    setSubjects([]);
    setPdfList([]);
  }, []);

  const value = {
    // Selected items with full data
    selectedStandard,
    selectedSubject,
    selectedPdfs,

    // Lists
    standards,
    subjects,
    pdfList,

    // Setters
    setStandards,
    setSubjects,
    setPdfList,

    // Actions
    selectStandard,
    selectSubject,
    togglePdf,
    clearSelections,

    // Helper getters
    getSelectedStandardId: () => selectedStandard?.id || "",
    getSelectedStandardName: () =>
      selectedStandard?.name || selectedStandard?.class_name || "",
    getSelectedSubjectId: () => selectedSubject?.id || "",
    getSelectedSubjectName: () =>
      selectedSubject?.name || selectedSubject?.subject_name || "",
    getSelectedPdfIds: () => selectedPdfs.map((pdf) => pdf.id),
  };

  return <ExamContext.Provider value={value}>{children}</ExamContext.Provider>;
};

export default ExamContext;
